import React, { useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { useParams } from 'react-router-dom'
import { generateReimbursementPDF } from '../utils/ReimbursementPdf'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import ModalPDF from './ModalPDF'
import { DetailBackButton, DetailApprovalActions } from './DetailApprovalActions'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { getEditHistoryEntries } from '../utils/editHistory'

const DetailRbs = () => {
    const [userData, setUserData] = useState(null)
    const [reimbursementDetail, setReimbursementDetail] = useState(null)
    const [reviewers, setReviewers] = useState([])
    const [, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    // Bagian AN: riwayat edit (siapa mengedit, kapan, alasannya)
    const [editHistoryWithNames, setEditHistoryWithNames] = useState([])

    const { id } = useParams()
    const uid = localStorage.getItem('userUid')
    const userRole = localStorage.getItem('userRole')

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true)

                const userDocRef = doc(db, 'users', uid)
                const userSnapshot = await getDoc(userDocRef)

                if (!userSnapshot.exists()) {
                    throw new Error('User tidak ditemukan')
                }

                const reimbursementDocRef = doc(db, 'reimbursement', id)
                const reimbursementSnapshot = await getDoc(reimbursementDocRef)

                if (!reimbursementSnapshot.exists()) {
                    throw new Error('Data reimbursement tidak ditemukan')
                }

                const reimbursementData = reimbursementSnapshot.data()
                setUserData(userSnapshot.data())
                setReimbursementDetail(reimbursementData)

                const fetchReviewerNames = async (reviewerArray) => {
                    if (!Array.isArray(reviewerArray)) return []
                    const promises = reviewerArray.map(async (reviewerUid) => {
                        try {
                            const reviewerDocRef = doc(db, 'userDirectory', reviewerUid)
                            const reviewerSnapshot = await getDoc(reviewerDocRef)
                            return reviewerSnapshot.exists() ? reviewerSnapshot.data().nama : null
                        } catch (error) {
                            console.error(`Error fetching reviewer (${reviewerUid}):`, error)
                            return null
                        }
                    })
                    return Promise.all(promises)
                }

                const [reviewer1Names, reviewer2Names, validatorNames] = await Promise.all([
                    fetchReviewerNames(reimbursementData?.user?.reviewer1),
                    fetchReviewerNames(reimbursementData?.user?.reviewer2),
                    fetchReviewerNames(reimbursementData?.user?.validator)
                ])

                const validReviewerNames = [...reviewer1Names, ...reviewer2Names].filter((name) => name !== null)
                setReviewers({
                    reviewerNames: validReviewerNames,
                    validatorNames: validatorNames.filter((name) => name !== null),
                    reviewer1Names: reviewer1Names.filter((name) => name !== null),
                    reviewer2Names: reviewer2Names.filter((name) => name !== null)
                })
            } catch (error) {
                console.error('Error fetching data:', error)
                setError(error.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (uid && id) {
            fetchData()
        }
    }, [uid, id])

    // Bagian AN: resolve nama editor (uid -> nama) untuk tiap entri riwayat
    // edit di statusHistory.
    useEffect(() => {
        const resolveEditHistoryNames = async () => {
            const entries = getEditHistoryEntries(reimbursementDetail?.statusHistory)
            if (entries.length === 0) {
                setEditHistoryWithNames([])
                return
            }

            const withNames = await Promise.all(
                entries.map(async (entry) => {
                    let actorName = 'N/A'
                    try {
                        const actorDocRef = doc(db, 'userDirectory', entry.actor)
                        const actorSnapshot = await getDoc(actorDocRef)
                        if (actorSnapshot.exists()) actorName = actorSnapshot.data().nama
                    } catch (error) {
                        console.error('Error fetching editor name:', error)
                    }
                    return { ...entry, actorName }
                })
            )

            setEditHistoryWithNames(withNames)
        }

        resolveEditHistoryNames()
    }, [reimbursementDetail])

    // Fungsi untuk mendapatkan status dengan next approver
    const getStatusWithNextApprover = (reimbursement, reviewerData) => {
        if (!reimbursement || !reviewerData) return 'N/A'

        const status = reimbursement.status
        const { validatorNames, reviewer1Names, reviewer2Names } = reviewerData

        // Helper untuk format nama
        const formatNames = (names) => {
            if (!names || names.length === 0) return ''
            if (names.length === 1) return names[0]
            if (names.length === 2) return `${names[0]} dan ${names[1]}`
            const lastIndex = names.length - 1
            return `${names.slice(0, lastIndex).join(', ')}, dan ${names[lastIndex]}`
        }

        switch (status) {
            case 'Diajukan':
                if (validatorNames && validatorNames.length > 0) {
                    return `Diajukan (Menunggu validasi ${formatNames(validatorNames)})`
                }
                return 'Diajukan'

            case 'Divalidasi':
                if (reviewer1Names && reviewer1Names.length > 0) {
                    return `Divalidasi (Menunggu approval ${formatNames(reviewer1Names)})`
                }
                return 'Divalidasi'

            case 'Diproses':
                // Jika ada reviewer2, berarti masih menunggu reviewer2 222
                if (reviewer2Names && reviewer2Names.length > 0) {
                    return `Diproses (Menunggu approval ${formatNames(reviewer2Names)})`
                }
                // Jika tidak ada reviewer2, langsung ke Super Admin
                return 'Diproses (Menunggu approval Super Admin)'

            case 'Disetujui':
                return 'Disetujui (Selesai)'

            case 'Ditolak':
                return 'Ditolak'

            case 'Dibatalkan':
                return 'Dibatalkan'

            default:
                return status || 'N/A'
        }
    }

    // Fungsi untuk mendapatkan status approval dengan nama reviewer
    const getDetailedApprovalStatus = (reimbursement, reviewerNames) => {
        if (!reimbursement || !reimbursement.statusHistory || reimbursement.statusHistory.length === 0) {
            return '-'
        }

        const lastStatus = reimbursement.statusHistory[reimbursement.statusHistory.length - 1]
        const { status, actor } = lastStatus

        const determineApprover = (reviewerArray, roleIndexStart) => {
            const reviewerIndex = reviewerArray.findIndex((uid) => uid === actor)
            if (reviewerIndex !== -1 && reviewerNames.reviewerNames) {
                return reviewerNames.reviewerNames[roleIndexStart + reviewerIndex] || 'N/A'
            }
            return '-'
        }

        const determineValidator = (validatorArray, actor) => {
            const validatorIndex = validatorArray.findIndex((uid) => uid === actor)
            if (validatorIndex !== -1 && reviewers.validatorNames && reviewers.validatorNames[validatorIndex]) {
                return reviewers.validatorNames[validatorIndex]
            }
            return 'N/A'
        }

        const reviewer1Array = reimbursement?.user?.reviewer1 || []
        const reviewer2Array = reimbursement?.user?.reviewer2 || []
        const validatorArray = reimbursement?.user?.validator || []

        const reviewer2Exists = Array.isArray(reviewer2Array) && reviewer2Array.some((uid) => uid)

        if (reimbursement.approvedByReviewer1Status === 'reviewer' && reimbursement.approvedByReviewer1) {
            const reviewer1 = determineApprover(reviewer1Array, 0)
            if (reviewer1 !== '-') return reviewer1
        }

        switch (reimbursement.status) {
            case 'Ditolak': {
                if (status.includes('Super Admin')) {
                    return 'Super Admin'
                }
                if (status.includes('Validator')) {
                    return determineValidator(validatorArray, actor)
                }
                if (status.includes('Reviewer 1')) {
                    return determineApprover(reviewer1Array, 0)
                }
                if (status.includes('Reviewer 2')) {
                    return determineApprover(reviewer2Array, reviewer1Array.length)
                }
                break
            }

            case 'Divalidasi': {
                if (status.includes('Super Admin')) {
                    return 'Super Admin'
                }
                if (status.includes('Validator')) {
                    return determineValidator(validatorArray, actor)
                }
                break
            }

            case 'Diproses': {
                if (reimbursement.approvedBySuperAdmin) {
                    return 'Super Admin'
                }
                if (reimbursement.approvedByReviewer1) {
                    return determineApprover(reviewer1Array, 0)
                }
                break
            }

            case 'Disetujui': {
                if (status.includes('Super Admin')) {
                    return 'Super Admin'
                }
                if (!reviewer2Exists && status.includes('Reviewer 1')) {
                    return determineApprover(reviewer1Array, 0)
                }
                if (status.includes('Reviewer 2')) {
                    return determineApprover(reviewer2Array, reviewer1Array.length)
                }
                break
            }

            default:
                return '-'
        }

        return '-'
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date)
    }

    const [modalPdfUrl, setModalPdfUrl] = useState(null)
    const [modalTitle, setModalTitle] = useState('')

    const handleViewAttachment = (lampiranUrl) => {
        if (lampiranUrl) {
            setModalPdfUrl(lampiranUrl)
            setModalTitle(`Lampiran ${reimbursementDetail.displayId}`)
        } else {
            toast.error('Lampiran tidak tersedia')
        }
    }

    const closePreview = () => {
        setModalPdfUrl(null)
        setModalTitle('')
    }

    const handleGenerateAndPreviewPDF = async () => {
        setIsLoading(true)
        try {
            setIsLoading(true)
            const url = await generateReimbursementPDF(reimbursementDetail)

            if (url) {
                setModalPdfUrl(url)
                setModalTitle(`Preview ${reimbursementDetail.displayId}`)
            }
        } catch (error) {
            toast.error('Gagal menghasilkan PDF')
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const getColumns = (kategori) => {
        const baseColumns = [
            { header: 'No.', key: 'no' },
            { header: 'Jenis Reimbursement', key: 'jenis' },
            { header: 'Tanggal Aktivitas', key: 'tanggal' }
        ]

        const categoryColumns = {
            bbm: [
                { header: 'Lokasi Pertamina', key: 'lokasi' },
                { header: 'Plat Nomor', key: 'plat' },
                { header: 'Liter', key: 'liter' }
            ],
            operasional: [
                { header: 'Kebutuhan', key: 'kebutuhan' },
                { header: 'Keterangan', key: 'keterangan' }
            ],
            'ga/umum': [
                { header: 'Item', key: 'item' },
                { header: 'Keterangan', key: 'keterangan' }
            ],
            default: []
        }

        const additionalColumns = categoryColumns[kategori?.toLowerCase()] || categoryColumns.default
        return [...baseColumns, ...additionalColumns, { header: 'Biaya', key: 'biaya' }]
    }

    const renderCell = (item, column, index) => {
        switch (column.key) {
            case 'no':
                return <div className="text-center">{index + 1}</div>
            case 'tanggal':
                return formatDate(item[column.key])
            case 'biaya':
                return `Rp${item[column.key]?.toLocaleString('id-ID') || 'N/A'}`
            case 'item':
            case 'kebutuhan':
                // Item RBS Operasional/GA-Umum berjenis BBM tidak punya kebutuhan/item (field disembunyikan
                // di form, diganti Plat & Liter) -- tampilkan info itu di sini biar tetap kelihatan di Detail.
                if (item.plat) {
                    return (
                        <div>
                            <div>{item[column.key] || '-'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                Plat: {item.plat} &middot; Liter: {item.liter || '-'}
                            </div>
                        </div>
                    )
                }
                return item[column.key] || 'N/A'
            case 'keterangan':
                if (!item.keterangan) return ''
                return (
                    <div className="whitespace-pre-wrap">
                        {item.keterangan}
                    </div>
                )
            default:
                return item[column.key] || 'N/A'
        }
    }

    if (!userData) {
        return (
            <div className="container mx-auto py-10 px-4 md:py-8 md:px-0">
                <h2 className="text-xl font-medium mb-4 dark:text-gray-100">
                    Detail <span className="font-bold">Reimbursement</span>
                </h2>
                <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg mb-6 shadow-sm transition-colors">
                    <div className="hidden xl:block">
                        <div className="grid grid-cols-2 gap-x-16 mb-4 font-medium">
                            <div className="grid grid-cols-[auto_1fr] gap-x-16">
                                {[...Array(5)].map((_, index) => (
                                    <React.Fragment key={`desktop-left-${index}`}>
                                        <Skeleton width={120} height={24} />
                                        <Skeleton width={200} height={24} />
                                    </React.Fragment>
                                ))}
                            </div>
                            <div className="grid grid-cols-[auto_1fr] gap-x-16">
                                {[...Array(5)].map((_, index) => (
                                    <React.Fragment key={`desktop-right-${index}`}>
                                        <Skeleton width={120} height={24} />
                                        <Skeleton width={200} height={24} />
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="hidden md:block xl:hidden">
                        <div className="grid grid-cols-2 gap-x-8 mb-4">
                            <div className="space-y-1">
                                {[...Array(5)].map((_, index) => (
                                    <div key={`tablet-left-${index}`} className="flex items-center">
                                        <Skeleton width={100} height={20} className="mr-2" />
                                        <Skeleton width={150} height={20} />
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-1">
                                {[...Array(5)].map((_, index) => (
                                    <div key={`tablet-right-${index}`} className="flex items-center">
                                        <Skeleton width={100} height={20} className="mr-2" />
                                        <Skeleton width={150} height={20} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="md:hidden">
                        <div className="space-y-1 mb-4">
                            {[...Array(10)].map((_, index) => (
                                <div
                                    key={`mobile-${index}`}
                                    className="grid grid-cols-[120px_1fr] gap-x-1 text-sm items-start"
                                >
                                    <Skeleton width={100} height={20} />
                                    <Skeleton width={'100%'} height={20} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="overflow-x-auto -mx-4 md:mx-0 mb-8">
                        <div className="min-w-[640px] md:w-full p-4 md:p-0">
                            <div className="bg-gray-100 dark:bg-gray-700 grid grid-cols-6 rounded-t-lg">
                                {[...Array(6)].map((_, index) => (
                                    <div key={`header-${index}`} className="p-2">
                                        <Skeleton height={24} />
                                    </div>
                                ))}
                            </div>

                            {[...Array(3)].map((_, rowIndex) => (
                                <div key={`row-${rowIndex}`} className="grid grid-cols-6 border-b dark:border-gray-600">
                                    {[...Array(6)].map((_, colIndex) => (
                                        <div key={`cell-${rowIndex}-${colIndex}`} className="p-2">
                                            <Skeleton height={20} />
                                        </div>
                                    ))}
                                </div>
                            ))}

                            <div className="grid grid-cols-6 mt-4">
                                <div className="col-span-5 p-2 text-right">
                                    <Skeleton width={120} height={24} className="ml-auto" />
                                </div>
                                <div className="p-2">
                                    <Skeleton height={24} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:justify-end space-y-2 md:space-y-0 md:space-x-2 mt-4">
                        <div className="w-full md:w-[170px]">
                            <Skeleton height={45} className="w-full" />
                        </div>
                        <div className="w-full md:w-[170px]">
                            <Skeleton height={45} className="w-full" />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const columns = getColumns(reimbursementDetail?.kategori)

    return (
        <div className="container mx-auto py-10 md:py-8">
            <DetailBackButton />
            <h2 className="text-xl font-medium mb-4 dark:text-gray-100">
                Detail <span className="font-bold">Reimbursement</span>
            </h2>

            <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg shadow transition-colors">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-x-16 mb-6 font-medium">
                    <div className="xl:hidden">
                        <div className="flex flex-wrap justify-between gap-1 md:gap-x-12">
                            <div className="space-y-1 flex-1">
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Nomor Dokumen</p>
                                    <p className="text-left">:</p>
                                    <p className="break-all">{reimbursementDetail?.displayId ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Nama Lengkap</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{reimbursementDetail?.user?.nama ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Department</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">
                                        {Array.isArray(reimbursementDetail?.user?.department) &&
                                            reimbursementDetail.user.department.length > 0
                                            ? reimbursementDetail.user.department.join(', ')
                                            : ''}
                                    </p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Unit Bisnis</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{reimbursementDetail?.user?.unit ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Tgl Pengajuan</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">
                                        {formatDate(reimbursementDetail?.tanggalPengajuan) ?? 'N/A'}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-1 flex-1">
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Kategori</p>
                                    <p className="text-left">:</p>
                                    <p className="break-all">{reimbursementDetail?.kategori ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Nomor Rekening</p>
                                    <p className="text-left">:</p>
                                    <p className="break-all">{reimbursementDetail?.user?.accountNumber ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Nama Bank</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{reimbursementDetail?.user?.bankName ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Status</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{getStatusWithNextApprover(reimbursementDetail, reviewers)}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>
                                        {reimbursementDetail?.status === 'Ditolak'
                                            ? 'Ditolak Oleh'
                                            : reimbursementDetail?.status === 'Divalidasi'
                                                ? 'Divalidasi Oleh'
                                                : 'Disetujui Oleh'}
                                    </p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">
                                        {getDetailedApprovalStatus(reimbursementDetail, reviewers)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hidden xl:grid grid-cols-[auto_1fr] gap-x-16 text-base">
                        <p>Nomor Dokumen</p>
                        <p>: {reimbursementDetail?.displayId ?? 'N/A'}</p>
                        <p>Nama Lengkap</p>
                        <p>: {reimbursementDetail?.user?.nama ?? 'N/A'}</p>
                        <p>Department</p>
                        <p>
                            :{' '}
                            {Array.isArray(reimbursementDetail?.user?.department) &&
                                reimbursementDetail.user.department.length > 0
                                ? reimbursementDetail.user.department.join(', ')
                                : ''}
                        </p>
                        <p>Unit Bisnis</p>
                        <p>: {reimbursementDetail?.user?.unit ?? 'N/A'}</p>
                        <p>Tanggal Pengajuan</p>
                        <p>: {formatDate(reimbursementDetail?.tanggalPengajuan) ?? 'N/A'}</p>
                    </div>
                    <div className="hidden xl:grid grid-cols-[auto_1fr] gap-x-16 text-base">
                        <p>Kategori Reimbursement</p>
                        <p>: {reimbursementDetail?.kategori ?? 'N/A'}</p>
                        <p>Nomor Rekening</p>
                        <p>: {reimbursementDetail?.user?.accountNumber ?? 'N/A'}</p>
                        <p>Nama Bank</p>
                        <p>: {reimbursementDetail?.user?.bankName ?? 'N/A'}</p>
                        <p>Status</p>
                        <p>: {getStatusWithNextApprover(reimbursementDetail, reviewers)}</p>
                        <p>
                            {reimbursementDetail?.status === 'Ditolak'
                                ? 'Ditolak Oleh'
                                : reimbursementDetail?.status === 'Divalidasi'
                                    ? 'Divalidasi Oleh'
                                    : 'Disetujui Oleh'}
                        </p>
                        <p>: {getDetailedApprovalStatus(reimbursementDetail, reviewers)}</p>
                    </div>
                </div>

                {/* Bagian AN: Riwayat Edit -- hanya tampil kalau pengajuan ini
                    pernah diedit ADMIN setelah dibuat (isEditMode di form). */}
                {editHistoryWithNames.length > 0 && (
                    <div className="mb-8 p-4 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <p className="font-medium text-amber-800 dark:text-amber-300 mb-2">
                            Riwayat Edit ({editHistoryWithNames.length}x)
                        </p>
                        <ul className="space-y-2 text-sm">
                            {editHistoryWithNames.map((entry, index) => (
                                <li key={index} className="text-gray-700 dark:text-gray-200">
                                    <span className="font-medium">
                                        {new Date(entry.timestamp).toLocaleString('id-ID', {
                                            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                        })}
                                    </span>
                                    {' -- '}{entry.actorName} ({entry.status.replace('Data Diubah oleh ', '')})
                                    {entry.reason && (
                                        <span className="block text-gray-600 dark:text-gray-400 italic">"{entry.reason}"</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="mb-8 overflow-x-auto -mx-4 md:mx-0">
                    <div className="min-w-[640px] md:w-full p-4 md:p-0">
                        <table className="w-full bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg text-sm dark:text-gray-200">
                            <thead>
                                <tr className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-100">
                                    {columns.map((column) => (
                                        <th
                                            key={column.key}
                                            className={`px-4 py-2 border ${column.key === 'no' ? 'text-center w-12' : 'text-left'}`}
                                        >
                                            {column.header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {reimbursementDetail?.reimbursements?.map((item, index) => (
                                    <tr key={index}>
                                        {columns.map((column) => (
                                            <td key={column.key} className="px-4 py-2 border dark:border-gray-600">
                                                {renderCell(item, column, index)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={columns.length} className="px-4 py-4"></td>
                                </tr>

                                {(reimbursementDetail?.status === 'Dibatalkan' || reimbursementDetail?.status === 'Ditolak') && (
                                    <tr>
                                        <td colSpan={columns.length} className="px-4 py-2 text-left border dark:border-gray-600">
                                            <span className="font-semibold">
                                                {reimbursementDetail?.status === 'Dibatalkan' ? 'Alasan Pembatalan :' : 'Alasan Penolakan :'}
                                            </span>{' '}
                                            {reimbursementDetail?.status === 'Dibatalkan'
                                                ? reimbursementDetail?.cancelReason
                                                : reimbursementDetail?.rejectReason}
                                        </td>
                                    </tr>
                                )}

                                <tr className="font-semibold">
                                    <td colSpan={columns.length - 1} className="px-4 py-2 text-right border dark:border-gray-600">
                                        Total Biaya :
                                    </td>
                                    <td className="px-4 py-2 border dark:border-gray-600">
                                        Rp{reimbursementDetail?.totalBiaya?.toLocaleString('id-ID')}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:justify-end mt-6 space-y-2 md:space-y-0 md:space-x-2">
                    <DetailApprovalActions docType="rbs" item={reimbursementDetail ? { ...reimbursementDetail, id } : null} />

                    <button
                        className={`w-full md:w-auto px-12 py-3 rounded ${
                            userData?.uid === reimbursementDetail?.user.uid || 
                            reimbursementDetail?.user.validator?.includes(userData?.uid) ||
                            userRole === 'Super Admin'
                                ? 'text-red-600 bg-transparent hover:text-red-800 border border-red-600 hover:border-red-800'
                                : 'text-white bg-red-600 hover:bg-red-700 hover:text-gray-200'
                        }`}
                        onClick={() => handleViewAttachment(reimbursementDetail?.lampiranUrl)}
                    >
                        Lihat Lampiran
                    </button>

                    {(userData?.uid === reimbursementDetail?.user.uid || 
                    reimbursementDetail?.user.validator?.includes(userData?.uid) ||
                    userRole === 'Super Admin') && (
                        <button
                            className={`w-full md:w-auto px-16 py-3 rounded ${
                                reimbursementDetail?.status === 'Disetujui'
                                    ? 'text-white bg-red-600 hover:bg-red-700 hover:text-gray-200'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            }`}
                            onClick={handleGenerateAndPreviewPDF}
                            disabled={reimbursementDetail?.status !== 'Disetujui' || isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="mr-1 animate-spin" />
                                    Loading..
                                </>
                            ) : (
                                'Print'
                            )}
                        </button>
                    )}
                </div>
            </div>

            <ModalPDF showModal={!!modalPdfUrl} previewUrl={modalPdfUrl} onClose={closePreview} title={modalTitle} />

        </div>
    )
}

export default DetailRbs